import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getElement } from "@/data/elements";
import { getOrgan } from "@/data/organs";
import { HABITS_BY_ELEMENT } from "@/data/practices";
import { FOODS } from "@/data/foods";
import { TEAS } from "@/data/teas";
import { elementColor } from "@/data/theme";
import { SourceTag, SafetyNote } from "@/components/common";

export default function ElementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const e = getElement(id);
  if (!e) return <div className="py-20 text-center text-muted-foreground">Elemento não encontrado. <Link to="/elementos" className="text-gold">Voltar</Link></div>;

  const c = elementColor(e.id);
  const habits = HABITS_BY_ELEMENT.find((h) => h.id === e.id);
  const food = FOODS.find((f) => f.id === e.id);
  const tea = TEAS.find((t) => t.elemento === e.id);

  return (
    <div className="space-y-8">
      <button onClick={() => navigate("/elementos")} data-testid="back-to-elements" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Cinco Elementos
      </button>

      <div className="overflow-hidden rounded-3xl border border-gold/25 p-8 sm:p-10" style={{ background: `linear-gradient(160deg, ${c.soft}, hsl(var(--card)))` }}>
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.2em]" style={{ color: c.primary }}>Elemento</p>
          <SourceTag pages={e.sourcePages} />
        </div>
        <h1 className="mt-2 font-serif text-5xl font-semibold" style={{ color: c.text }}>{e.name}</h1>
        <p className="mt-2 text-lg font-medium" style={{ color: c.primary }}>{e.palavras.join(" · ")}</p>
        {e.descricao && <p className="mt-4 max-w-2xl text-base leading-relaxed" style={{ color: c.text }}>{e.descricao}</p>}
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="rounded-3xl border border-border bg-card p-6">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Órgãos associados</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {e.orgaoIds.map((oid) => {
              const o = getOrgan(oid);
              return (
                <Link key={oid} to={`/orgaos/${oid}`} data-testid={`element-organ-${oid}`} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-transform hover:scale-105" style={{ backgroundColor: c.soft, color: c.text }}>
                  {o.name} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              );
            })}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl bg-secondary/60 p-3"><p className="text-xs text-muted-foreground">Emoção</p><p className="font-medium text-foreground">{e.emocao}</p></div>
            <div className="rounded-xl bg-secondary/60 p-3"><p className="text-xs text-muted-foreground">Sabor</p><p className="font-medium text-foreground">{e.sabor}</p></div>
          </div>
        </div>

        <div className="rounded-3xl border border-border bg-card p-6">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Qualidades positivas</p>
          <p className="mt-2 text-sm leading-relaxed text-foreground">{e.qualidades}</p>
          <p className="mt-4 text-xs uppercase tracking-wider text-muted-foreground">Desequilíbrio pode manifestar</p>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{e.desequilibrio}</p>
        </div>
      </div>

      {habits && (
        <div className="rounded-3xl border border-border bg-card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl font-medium">Hábitos simples — {habits.qualidade}</h2>
            <SourceTag pages={habits.sourcePages} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {habits.habitos.map((h, i) => (
              <span key={i} className="rounded-full bg-secondary px-3 py-1.5 text-sm text-foreground">{h}</span>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2">
        {food && (
          <div className="rounded-3xl border border-border bg-card p-6">
            <div className="flex items-center justify-between"><h2 className="font-serif text-xl font-medium">Alimentos</h2><SourceTag pages={food.sourcePages} /></div>
            <p className="mt-1 text-sm text-muted-foreground">Sabor {food.sabor}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {food.exemplos.map((a, i) => (<span key={i} className="rounded-full px-3 py-1.5 text-sm font-medium" style={{ backgroundColor: c.soft, color: c.text }}>{a}</span>))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">{food.nota}</p>
            <Link to="/alimentos" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-gold">Ver alimentos por elemento <ArrowRight className="h-4 w-4" /></Link>
          </div>
        )}
        {tea && (
          <div className="rounded-3xl border border-border bg-card p-6">
            <div className="flex items-center justify-between"><h2 className="font-serif text-xl font-medium">Chás citados</h2><SourceTag pages={tea.sourcePages} /></div>
            <div className="mt-3 flex flex-wrap gap-2">
              {tea.chas.map((t, i) => (<span key={i} className="rounded-full bg-secondary px-3 py-1.5 text-sm text-foreground capitalize">{t}</span>))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">{tea.cuidado}</p>
            <Link to="/chas" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-gold">Ver todos os chás <ArrowRight className="h-4 w-4" /></Link>
          </div>
        )}
      </div>

      <SafetyNote>Quando você compreende essa dança natural, pode fazer escolhas mais alinhadas. Conteúdo educativo — não substitui avaliação profissional.</SafetyNote>
    </div>
  );
}
